import { useState } from 'react';

import { ErroDeRede, preverBmp } from '../nucleo/api.js';
import {
  ANALISES,
  GRUPOS,
  lerDispersao,
  nomeDaAnalise,
  oQueMedirPrimeiro,
} from '../nucleo/laboratorio.js';
import { enxuto, lerNumero, plural, porcento } from '../nucleo/numero.js';

/**
 * Previsão de BMP por composição — e o roteiro do que medir em seguida.
 *
 * **Esta é a tela mais perigosa do app.** Ela devolve um número de metano sem
 * que ninguém tenha medido metano, e todo mundo que a usar vai querer tratar
 * esse número como medida. A literatura é inequívoca de que não é: Ziero et
 * al. (2026) acharam uma combinação "excelente" em 650, e 28 inaceitáveis.
 *
 * Então a tela foi desenhada em torno de três recusas:
 *
 * 1. **Não mostra média.** Mostra os seis modelos, cada um com o seu valor. A
 *    faixa e a discordância aparecem; o "resultado" não.
 * 2. **Não deixa a concordância virar precisão.** Dois laboratórios medindo o
 *    MESMO material discordam de 21 a 33%. Modelos que concordam dentro disso
 *    estão empatando com o ruído, e a tela diz isso em vez de celebrar.
 * 3. **Não pede as vinte análises de uma vez.** O formulário vazio de vinte
 *    campos é a tradução literal da API e a pior tela possível. Quem chega
 *    aqui tem um laudo e uma pergunta — "vale a pena pagar mais alguma
 *    análise?" —, e o roteiro responde ela com a conta honesta: quantos
 *    modelos cada medição destrava SOZINHA.
 */
export function LaboratorioPrever() {
  const [valores, setValores] = useState({});
  const [elementar, setElementar] = useState({});
  const [resposta, setResposta] = useState(null);
  const [erro, setErro] = useState(null);
  const [calculando, setCalculando] = useState(false);

  function mudar(campo, texto) {
    setValores((v) => ({ ...v, [campo]: texto }));
  }

  function enviar(evento) {
    evento.preventDefault();
    setCalculando(true);
    setErro(null);

    const corpo = { nome: 'material caracterizado' };
    for (const [campo, texto] of Object.entries(valores)) {
      const n = lerNumero(texto);
      if (n !== null) corpo[campo] = n;
    }
    const chnos = {};
    for (const [letra, texto] of Object.entries(elementar)) {
      const n = lerNumero(texto);
      if (n !== null) chnos[letra] = n;
    }
    if (Object.keys(chnos).length > 0) corpo.elementar = chnos;

    preverBmp(corpo)
      .then((r) => setResposta(r))
      .catch((e) => setErro(e))
      .finally(() => setCalculando(false));
  }

  return (
    <div className="empilha-largo">
      <section className="bloco">
        <h2 className="simulador__titulo">Quanto este material deve render?</h2>
        <p className="simulador__chamada">
          Preencha o que o seu laudo tiver. Cada análise que entra põe mais modelos para
          rodar — e a tela diz quantos, antes de você gastar com a próxima.
        </p>
      </section>

      <form className="bloco" onSubmit={enviar}>
        {GRUPOS.map((g) => (
          <fieldset className="grupo-de-analise" key={g.chave}>
            <legend className="rotulo-secao">{g.rotulo}</legend>
            <p className="campo__dica">{g.explicacao}</p>
            <div className="grupo-de-analise__campos">
              {Object.entries(ANALISES)
                .filter(([, a]) => a.grupo === g.chave)
                .map(([campo, a]) =>
                  campo === 'elementar' ? (
                    <Elementar key={campo} valores={elementar} aoMudar={setElementar} />
                  ) : (
                    <div className="campo" key={campo}>
                      <label className="campo__rotulo" htmlFor={campo}>
                        {a.rotulo} <span className="campo__opcional">{a.unidade}</span>
                      </label>
                      <input
                        id={campo}
                        className="campo__entrada"
                        inputMode="decimal"
                        value={valores[campo] ?? ''}
                        onChange={(e) => mudar(campo, e.target.value)}
                      />
                    </div>
                  ),
                )}
            </div>
          </fieldset>
        ))}

        <button type="submit" className="botao" disabled={calculando}>
          {calculando ? 'Calculando…' : 'Rodar o comitê de modelos'}
        </button>
      </form>

      {erro ? <Falhou erro={erro} /> : null}
      {resposta ? <Resultado resposta={resposta} /> : null}
    </div>
  );
}

/**
 * CHNOS em cinco campos, e não em um.
 *
 * Os cinco vão juntos ou não vão: a API recusa meia análise elementar com um
 * 422, e um formulário que aceitasse três letras produziria um erro de
 * validação em vez de um resultado. Ficam lado a lado para que a ausência de
 * um seja visível antes de enviar.
 */
function Elementar({ valores, aoMudar }) {
  const LETRAS = [
    ['c', 'Carbono'],
    ['h', 'Hidrogênio'],
    ['o', 'Oxigênio'],
    ['n', 'Nitrogênio'],
    ['s', 'Enxofre'],
  ];
  return (
    <div className="elementar">
      <p className="campo__rotulo">
        Análise elementar <span className="campo__opcional">% do SV</span>
      </p>
      <p className="campo__dica">
        Sem ela não sai o teto físico de Boyle, e dois dos seis modelos ficam parados: eles
        preveem biodegradabilidade e só viram metano multiplicando pelo teto.
      </p>
      <div className="elementar__campos">
        {LETRAS.map(([letra, nome]) => (
          <div className="campo" key={letra}>
            <label className="campo__rotulo" htmlFor={`el-${letra}`}>
              {nome}
            </label>
            <input
              id={`el-${letra}`}
              className="campo__entrada"
              inputMode="decimal"
              value={valores[letra] ?? ''}
              onChange={(e) => aoMudar((v) => ({ ...v, [letra]: e.target.value }))}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function Falhou({ erro }) {
  const rede = erro instanceof ErroDeRede;
  return (
    <section className="bloco" role="alert">
      <h3 className="rotulo-secao">O cálculo não saiu</h3>
      <p className="campo__dica campo__dica--erro">
        {rede
          ? 'Não consegui falar com o servidor. Se você está sem internet, tente de novo quando voltar.'
          : erro.message}
      </p>
    </section>
  );
}

/**
 * O resultado: a faixa primeiro, a discordância junto, nunca a média.
 *
 * A ordem é deliberada. O primeiro número que a pessoa lê é a FAIXA, porque é
 * a resposta honesta; o segundo é a discordância, porque é o que diz se a
 * faixa significa alguma coisa. Um "valor central" em corpo grande no topo
 * seria lido como o resultado e tudo embaixo viraria rodapé.
 */
function Resultado({ resposta }) {
  const aplicados = resposta.modelos.filter((m) => m.aplicado);
  const leitura = lerDispersao(resposta.dispersao_pct);
  const roteiro = oQueMedirPrimeiro(resposta.modelos);

  // A lista modelo a modelo e o roteiro saem nos DOIS estados, e isso é
  // decisão de tela, não economia de código. No estado em que a tela abre
  // nenhum modelo roda — e era justamente aí que a versão anterior escondia
  // os seis, deixando a pessoa sem saber que existem nem o que cada um pede.
  // O estado vazio é o mais visitado; esconder informação nele é esconder
  // informação de quase todo mundo.
  return (
    <>
      <section className="bloco">
        {aplicados.length === 0 ? (
          <>
            <h3 className="rotulo-secao">Nenhum modelo pôde rodar</h3>
            <p className="afirmacao">{resposta.recado}</p>
          </>
        ) : (
          <>
            <h3 className="rotulo-secao">O que os modelos dizem</h3>
            <p className="ficha__linha">
              <span className="numero-grande">
                {enxuto(resposta.faixa[0])} a {enxuto(resposta.faixa[1])}
              </span>{' '}
              <span className="unidade">NmL CH₄ por g de SV</span>
            </p>
            <p className="terciario">
              {plural(aplicados.length, 'modelo rodou', 'modelos rodaram')} de{' '}
              {resposta.modelos.length}. Não existe um valor: existe esta faixa.
            </p>
          </>
        )}

        {leitura ? (
          <div className={`dispersao dispersao--${leitura.nivel}`}>
            <p className="serie__titulo">
              {porcento(resposta.dispersao_pct, 0)} de discordância — {leitura.rotulo}
            </p>
            {/* A EXPLICAÇÃO é a do servidor, de propósito. `lerDispersao` sabe
                escrever a mesma coisa (e escreve, onde não há servidor), mas
                duas prosas sobre o mesmo número divergem no dia em que alguém
                mexe numa só. O JS aqui decide a cor; a frase vem de quem fez
                a conta. */}
            <p className="campo__dica">{resposta.recado}</p>
          </div>
        ) : null}

        {resposta.teto_boyle != null ? (
          <p className="terciario">
            Teto físico de Boyle: {enxuto(resposta.teto_boyle)} NmL CH₄/g SV. Nenhuma
            previsão acima dele é possível — é estequiometria, não estatística.
          </p>
        ) : null}
      </section>

      <section className="bloco">
        <h3 className="rotulo-secao">Modelo a modelo</h3>
        <div className="empilha">
          {resposta.modelos.map((m) => (
            <Modelo key={m.chave} modelo={m} />
          ))}
        </div>
      </section>

      <Roteiro roteiro={roteiro} />

      {resposta.avisos.length > 0 ? (
        <section className="bloco">
          <h3 className="rotulo-secao">Avisos</h3>
          <div className="empilha">
            {resposta.avisos.map((a) => (
              <p key={a.codigo} className={a.grave ? 'campo__dica--erro' : 'campo__dica'}>
                {a.mensagem}
                {a.fonte ? <span className="serie__fonte"> {a.fonte}</span> : null}
              </p>
            ))}
          </div>
        </section>
      ) : null}
    </>
  );
}

function Modelo({ modelo }) {
  return (
    <div className={`serie ${modelo.aplicado ? '' : 'serie--parado'}`}>
      <p className="serie__titulo">{modelo.rotulo}</p>
      {modelo.aplicado ? (
        <p className="serie__numeros">
          <span>{enxuto(modelo.valor_sv)} NmL CH₄/g SV</span>
          {modelo.acima_do_teto ? (
            <span className="serie__perda">acima do teto físico</span>
          ) : null}
          {modelo.extrapolacoes?.length > 0 ? (
            <span className="serie__perda">fora da faixa de calibração</span>
          ) : null}
        </p>
      ) : (
        <p className="campo__dica">
          {modelo.faltando.length > 0
            ? `Parado por falta de: ${modelo.faltando.map(nomeDaAnalise).join(', ')}.`
            : modelo.motivo}
        </p>
      )}
      <p className="serie__fonte">{modelo.fonte}</p>
      {modelo.erro_declarado ? (
        <p className="campo__dica campo__dica--atencao">
          Erro declarado pelo autor: {modelo.erro_declarado}
        </p>
      ) : null}
    </div>
  );
}

/**
 * O roteiro de laboratório — e por que o número exibido é o menor dos dois.
 *
 * `desbloqueia` conta os modelos que passam a rodar com esta análise E NADA
 * MAIS. `participa` conta todos em que ela aparece. A lignina, no estado em
 * que a tela abre, participa de seis e desbloqueia três. Anunciar seis seria
 * cobrar uma análise por um retorno que ela não dá sozinha — e quem paga a
 * conta é o laboratório que acreditou na frase.
 */
function Roteiro({ roteiro }) {
  if (roteiro.length === 0) return null;
  return (
    <section className="bloco">
      <h3 className="rotulo-secao">O que medir primeiro</h3>
      <p className="terciario">
        Quantos modelos cada análise põe para rodar sozinha. A segunda coluna é em quantos
        ela aparece — se os dois números diferem, ela precisa de companhia.
      </p>
      {/* Quando NADA destrava sozinho, a lista inteira diz a mesma coisa e a
          promessa de "o que medir primeiro" fica sem resposta. O caso é real e
          comum: ele aparece assim que algumas análises já foram informadas, e
          o que sobra são modelos que pedem três ou quatro de uma vez. Dizer
          isso em uma linha é melhor que deixar sete linhas iguais explicarem
          sozinhas. */}
      {roteiro.every((r) => r.desbloqueia.length === 0) ? (
        <p className="campo__dica campo__dica--atencao">
          Nenhuma análise isolada destrava modelo novo daqui: os que sobraram pedem mais de
          uma de uma vez. Medir qualquer uma destas só vale junto com as outras do mesmo
          modelo — o que está entre parênteses no nome dele.
        </p>
      ) : null}
      <div className="empilha">
        {roteiro.map((r) => (
          <div className="roteiro__linha" key={r.campo}>
            <span className="roteiro__nome">{r.rotulo}</span>
            <span className="roteiro__conta">
              {r.desbloqueia.length > 0 ? (
                <strong>
                  destrava {plural(r.desbloqueia.length, 'modelo', 'modelos')}
                </strong>
              ) : (
                <span className="terciario">sozinha não destrava nada</span>
              )}
              {r.participa.length !== r.desbloqueia.length ? (
                <span className="terciario"> · participa de {r.participa.length}</span>
              ) : null}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
